import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import './BirthData.css';

/**
 * Convert local date/time in a specific IANA timezone to UTC
 * Uses Intl.DateTimeFormat to properly handle DST and timezone rules
 * @param {string} dateStr - Date in YYYY-MM-DD format
 * @param {string} timeStr - Time in HH:MM format
 * @param {string} timezone - IANA timezone (e.g., 'Europe/Helsinki')
 * @returns {string} ISO 8601 UTC datetime string
 */
const convertToUTC = (dateStr, timeStr, timezone) => {
  try {
    // Parse date and time components
    const [year, month, day] = dateStr.split('-').map(Number);
    const [hour, minute] = timeStr.split(':').map(Number);
    
    // Create a formatter for the target timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    
    // We need to find a UTC timestamp that, when formatted in the target timezone,
    // gives us our desired local time. We'll use an iterative approach.
    
    // Start with a UTC date at the same date/time (this is our initial guess)
    let utcCandidate = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    
    // Format this UTC date in the target timezone to see what local time it represents
    let formatted = formatter.format(utcCandidate);
    // Format is: MM/DD/YYYY, HH:MM:SS
    const parts = formatted.match(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})/);
    
    if (parts) {
      const tzMonth = parseInt(parts[1]);
      const tzDay = parseInt(parts[2]);
      const tzYear = parseInt(parts[3]);
      const tzHour = parseInt(parts[4]);
      const tzMinute = parseInt(parts[5]);
      
      // Calculate the difference between what we want and what we got
      const hourDiff = hour - tzHour;
      const minuteDiff = minute - tzMinute;
      const dayDiff = day - tzDay;
      const monthDiff = month - tzMonth;
      const yearDiff = year - tzYear;
      
      // Adjust the UTC candidate based on the difference
      // Convert all differences to minutes for easier calculation
      const totalMinutesDiff = (yearDiff * 365 * 24 * 60) + 
                               (monthDiff * 30 * 24 * 60) + 
                               (dayDiff * 24 * 60) + 
                               (hourDiff * 60) + 
                               minuteDiff;
      
      utcCandidate = new Date(utcCandidate.getTime() - (totalMinutesDiff * 60 * 1000));
      
      // Verify the result (one iteration should be enough for most cases)
      formatted = formatter.format(utcCandidate);
      const verifyParts = formatted.match(/(\d{2})\/(\d{2})\/(\d{4}), (\d{2}):(\d{2}):(\d{2})/);
      
      if (verifyParts) {
        const vHour = parseInt(verifyParts[4]);
        const vMin = parseInt(verifyParts[5]);
        // If still off, do one more fine adjustment
        if (vHour !== hour || vMin !== minute) {
          const finalDiff = ((hour - vHour) * 60) + (minute - vMin);
          utcCandidate = new Date(utcCandidate.getTime() - (finalDiff * 60 * 1000));
        }
      }
    }
    
    return utcCandidate.toISOString();
  } catch (error) {
    console.error('Error converting to UTC:', error, { dateStr, timeStr, timezone });
    // Fallback: create date and assume it's already in UTC (not ideal but safe)
    const [y, m, d] = dateStr.split('-').map(Number);
    const [h, min] = timeStr.split(':').map(Number);
    const fallbackDate = new Date(Date.UTC(y, m - 1, d, h, min, 0));
    return fallbackDate.toISOString();
  }
};

const BirthData = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get('returnTo');
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    lat: null,
    lng: null,
    timezone: 'Europe/Helsinki',
  });

  const [existingData, setExistingData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      try {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Hae olemassa oleva birth_data
        const { data, error: fetchError } = await supabase
          .from('birth_data')
          .select('*')
          .eq('user_id', currentUser.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (fetchError && fetchError.code !== 'PGRST116') {
          console.error('Error fetching birth data:', fetchError);
        }

        if (data) {
          setExistingData(data);
          
          // Format date for HTML input (YYYY-MM-DD)
          let formattedDate = data.date;
          if (formattedDate && typeof formattedDate === 'string') {
            // If date is in format YYYY-MM-DD, use as-is
            // If it's a Date object or other format, convert
            if (formattedDate.includes('T')) {
              formattedDate = formattedDate.split('T')[0];
            }
          } else if (formattedDate instanceof Date) {
            formattedDate = formattedDate.toISOString().split('T')[0];
          }
          
          // Format time for HTML input (HH:MM)
          let formattedTime = data.time;
          if (formattedTime && typeof formattedTime === 'string') {
            // Ensure time is in HH:MM format (remove seconds if present)
            if (formattedTime.includes(':')) {
              const parts = formattedTime.split(':');
              formattedTime = `${parts[0].padStart(2, '0')}:${parts[1] || '00'}`;
            } else if (formattedTime.length === 4) {
              // Handle HHMM format
              formattedTime = `${formattedTime.substring(0, 2)}:${formattedTime.substring(2)}`;
            }
          }
          
          setFormData({
            date: formattedDate || '',
            time: formattedTime || '',
            location: data.location || '',
            lat: data.lat ?? null,
            lng: data.lng ?? null,
            timezone: data.timezone || 'Europe/Helsinki',
          });
        }
      } catch (err) {
        console.error('BirthData: Error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) {
      console.warn('BirthData: Save already in progress, ignoring duplicate submit');
      return;
    }

    setError('');
    setSuccess(false);

    // LOG: Pre-validation state
    console.log('=== BIRTH DATA SAVE: PRE-VALIDATION ===');
    console.log('Form state:', {
      date: formData.date,
      time: formData.time,
      location: formData.location,
      lat: formData.lat,
      lng: formData.lng,
      timezone: formData.timezone,
    });
    console.log('User:', user ? { id: user.id, email: user.email } : 'null');
    console.log('Existing data:', existingData ? { id: existingData.id } : 'null');
    console.log('Supabase client:', supabase ? 'initialized' : 'null');

    if (!supabase || !user) {
      const errorMsg = 'Et ole kirjautunut.';
      console.error('Validation failed: Not authenticated');
      setError(errorMsg);
      return;
    }

    // Validation: Check that required fields are non-empty strings
    const dateTrimmed = formData.date ? formData.date.trim() : '';
    const timeTrimmed = formData.time ? formData.time.trim() : '';
    const locationTrimmed = formData.location ? formData.location.trim() : '';
    
    if (!dateTrimmed || !timeTrimmed || !locationTrimmed) {
      const errorMsg = 'Täytä kaikki pakolliset kentät (päivä, aika, paikka).';
      console.error('Validation failed: Missing required fields', {
        date: dateTrimmed,
        time: timeTrimmed,
        location: locationTrimmed,
        hasDate: !!dateTrimmed,
        hasTime: !!timeTrimmed,
        hasLocation: !!locationTrimmed,
      });
      setError(errorMsg);
      return;
    }

    setSaving(true);

    try {
      // Tarkista user.id auth.uid() perusteella (RLS-yhteensopivuus)
      const { data: { user: authUser }, error: authError } = await supabase.auth.getUser();
      if (authError || !authUser) {
        console.error('Auth check failed:', authError);
        throw new Error('Autentikointi epäonnistui');
      }

      // Auto-geocode location if location is provided and coordinates are missing
      let geocodedLat = formData.lat;
      let geocodedLng = formData.lng;
      let geocodeWarning = null;

      if (locationTrimmed && (geocodedLat === null || geocodedLat === undefined || geocodedLng === null || geocodedLng === undefined)) {
        console.log('=== AUTO-GEOCODING LOCATION ===');
        console.log('Location string:', locationTrimmed);
        
        try {
          const geocodeResponse = await fetch('/api/geocode', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query: locationTrimmed }),
          });

          if (!geocodeResponse.ok) {
            console.warn('Geocode API error:', geocodeResponse.status);
          } else {
            const geocodeResult = await geocodeResponse.json();
            
            if (geocodeResult.ok && geocodeResult.lat !== null && geocodeResult.lng !== null) {
              geocodedLat = geocodeResult.lat;
              geocodedLng = geocodeResult.lng;
              console.log('Geocoding successful:', { lat: geocodedLat, lng: geocodedLng, displayName: geocodeResult.displayName });
            } else {
              console.warn('Geocoding failed or no results:', geocodeResult.error || 'Location not found');
              geocodeWarning = 'Sijainnin koordinaatteja ei löytynyt. Nousumerkki ja huoneet eivät ole käytettävissä ennen kuin sijainti löytyy.';
            }
          }
        } catch (geocodeError) {
          console.error('Geocoding request failed:', geocodeError);
          geocodeWarning = 'Sijainnin koordinaatteja ei löytynyt. Nousumerkki ja huoneet eivät ole käytettävissä ennen kuin sijainti löytyy.';
        }
      }

      // Normalize time format (ensure HH:MM for legacy field)
      let normalizedTime = timeTrimmed;
      if (normalizedTime && !normalizedTime.includes(':')) {
        // Handle HHMM format
        if (normalizedTime.length === 4) {
          normalizedTime = `${normalizedTime.substring(0, 2)}:${normalizedTime.substring(2)}`;
        }
      }
      // Remove seconds if present (HH:MM:SS -> HH:MM) for legacy field
      if (normalizedTime.includes(':') && normalizedTime.split(':').length > 2) {
        const parts = normalizedTime.split(':');
        normalizedTime = `${parts[0]}:${parts[1]}`;
      }
      
      // Normalize time to HH:MM:SS format for canonical birth_time field
      let birthTime = normalizedTime;
      if (!birthTime.includes(':')) {
        // Handle HHMM format
        if (birthTime.length === 4) {
          birthTime = `${birthTime.substring(0, 2)}:${birthTime.substring(2)}:00`;
        } else {
          birthTime = `${birthTime}:00:00`;
        }
      } else {
        const timeParts = birthTime.split(':');
        if (timeParts.length === 2) {
          // Add seconds if missing
          birthTime = `${birthTime}:00`;
        } else if (timeParts.length > 3) {
          // Keep only HH:MM:SS
          birthTime = `${timeParts[0]}:${timeParts[1]}:${timeParts[2]}`;
        }
      }
      
      // Compute canonical UTC datetime from date, time, and timezone
      const timezone = formData.timezone || 'Europe/Helsinki';
      const birth_datetime_utc = convertToUTC(dateTrimmed, normalizedTime, timezone);
      
      console.log('=== BIRTH DATA: UTC CONVERSION ===');
      console.log('Input:', { date: dateTrimmed, time: normalizedTime, timezone });
      console.log('Output UTC:', birth_datetime_utc);
      
      // Build payload with BOTH legacy and canonical fields
      // Default birth_time_accuracy to 'exact' when user provides a time
      // Preserve existing value if updating, otherwise default to 'exact'
      const birthData = {
        // Legacy fields (required by current schema)
        user_id: authUser.id, // Käytä auth.uid() - RLS vaatii tämän
        date: dateTrimmed,
        time: normalizedTime,
        location: locationTrimmed,
        lat: geocodedLat ?? null, // Use geocoded coordinates if available
        lng: geocodedLng ?? null, // Use geocoded coordinates if available
        timezone: timezone,
        // Preserve existing birth_time_accuracy if updating, otherwise default to 'exact'
        birth_time_accuracy: existingData?.birth_time_accuracy || 'exact',
        
        // Canonical fields (birth_* prefix)
        birth_date: dateTrimmed,
        birth_time: birthTime, // HH:MM:SS format
        birth_place: locationTrimmed,
        birth_timezone: timezone,
        birth_datetime_utc: birth_datetime_utc, // Canonical UTC timestamp
      };

      // LOG: Pre-save payload
      console.log('=== BIRTH DATA SAVE: PRE-SAVE ===');
      console.log('Payload:', birthData);
      console.log('Operation:', existingData ? 'UPDATE' : 'INSERT');
      if (existingData) {
        console.log('Existing record ID:', existingData.id);
      }

      let result;
      if (existingData) {
        // Update - RLS tarkistaa automaattisesti että user_id = auth.uid()
        console.log('Executing UPDATE...');
        result = await supabase
          .from('birth_data')
          .update(birthData)
          .eq('id', existingData.id)
          .eq('user_id', authUser.id) // Ylimääräinen turvallisuus
          .select()
          .single(); // Get single record back

        if (result.error) {
          console.error('UPDATE error:', result.error);
          throw result.error;
        }
        console.log('UPDATE successful:', result.data);
      } else {
        // Insert - RLS tarkistaa automaattisesti että user_id = auth.uid()
        console.log('Executing INSERT...');
        result = await supabase
          .from('birth_data')
          .insert(birthData)
          .select()
          .single(); // Get single record back

        if (result.error) {
          console.error('INSERT error:', result.error);
          throw result.error;
        }
        console.log('INSERT successful:', result.data);
      }

      // LOG: Post-save success
      console.log('=== BIRTH DATA SAVE: SUCCESS ===');
      console.log('Result:', result.data);

      // Show warning if geocoding failed (but save was successful)
      if (geocodeWarning) {
        setError(geocodeWarning); // Use error state to show warning (non-blocking)
        // Still show success after a delay
        setTimeout(() => {
          setError('');
          setSuccess(true);
        }, 5000);
      } else {
        setSuccess(true);
      }
      
      // Handle returnTo redirect after successful save
      if (returnTo && result.data) {
        const savedId = result.data.id;
        // Redirect to returnTo with birthId parameter
        const returnUrl = returnTo.includes('?') 
          ? `${returnTo}&birthId=${savedId}`
          : `${returnTo}?birthId=${savedId}`;
        setTimeout(() => {
          navigate(returnUrl);
        }, 1000); // Small delay to show success message
        return; // Exit early since we're redirecting
      }
      
      // Update existingData state for future updates
      if (result.data) {
        // result.data is a single object when using .single()
        setExistingData(result.data);
        // Also update form data to reflect saved values
        setFormData({
          date: result.data.date || dateTrimmed,
          time: result.data.time || normalizedTime,
          location: result.data.location || locationTrimmed,
          lat: result.data.lat ?? null,
          lng: result.data.lng ?? null,
          timezone: result.data.timezone || formData.timezone,
        });
      } else if (existingData) {
        // Fallback if result.data is missing
        setExistingData({ ...existingData, ...birthData });
      }
    } catch (err) {
      console.error('=== BIRTH DATA SAVE: ERROR ===');
      console.error('Error details:', {
        message: err.message,
        code: err.code,
        details: err.details,
        hint: err.hint,
        fullError: err,
      });
      setError(`Tallennus epäonnistui: ${err.message || 'Tuntematon virhe'}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="moon-loader">
          <div className="moon"></div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="birth-data-page">
      <div className="container">
        <h1>Syntymätiedot</h1>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">✅ Tiedot tallennettu onnistuneesti!</div>}

        <form onSubmit={handleSubmit} className="birth-data-form">
          <div className="form-group">
            <label>Syntymäpäivä *</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
              max={new Date().toISOString().split('T')[0]}
            />
          </div>

          <div className="form-group">
            <label>Syntymäaika *</label>
            <input
              type="time"
              value={formData.time}
              onChange={(e) => setFormData({ ...formData, time: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Syntymäpaikka *</label>
            <input
              type="text"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="Esim. Helsinki, Suomi"
              required
            />
            <small>Sijainti haetaan automaattisesti. Jos koordinaatteja ei löydy, nousumerkki ja huoneet voivat olla puutteellisia.</small>
          </div>

          <div className="form-group">
            <label>Aikavyöhyke</label>
            <select
              value={formData.timezone}
              onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
            >
              <option value="Europe/Helsinki">Europe/Helsinki (Suomi)</option>
              <option value="UTC">UTC</option>
              <option value="Europe/Stockholm">Europe/Stockholm</option>
              <option value="Europe/Oslo">Europe/Oslo</option>
            </select>
          </div>

          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Tallennetaan...' : existingData ? 'Päivitä tiedot' : 'Tallenna tiedot'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default BirthData;
